import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperButtonComponent, PaperIconComponent, PaperKbdComponent } from '@ricoh-paper/angular';
import { FlowMarkComponent } from '../../../../flow-v2/identity/flow-mark.component';
import { LocaleService } from '../../../../../core/services/locale.service';
import type { CmdkActionItem, CmdkNavItem } from '../models/cmdk.model';

/**
 * The preview for a registry-backed row — a navigation destination or an
 * action. Icon tile, name, what it is, where its shortcut works, and the one
 * button that runs it.
 *
 * Modest by design. The rich previews (a space's availability strip, a
 * person's floor-plan pin, a widget's live mock) are what the right-hand pane
 * exists for, and they arrive with their data in slices 4, 5 and 7. What this
 * one must not do is look like a placeholder for them: an action genuinely has
 * one thing to say and one thing to do, and padding it out with empty
 * scaffolding would be worse than being brief.
 */
@Component({
  selector: 'im-cmdk-preview-command',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FlowMarkComponent, PaperButtonComponent, PaperIconComponent, PaperKbdComponent],
  templateUrl: './cmdk-preview-command.component.html',
  styleUrl: './cmdk-preview-command.component.scss',
})
export class CmdkPreviewCommandComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  readonly item = input.required<CmdkNavItem | CmdkActionItem>();
  readonly activated = output<void>();

  protected readonly kindLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate(
      this.item().kind === 'nav' ? 'shell.cmdk.preview.navigation' : 'shell.cmdk.preview.action',
    );
  });

  protected readonly description = computed(() => {
    this.locale.locale();
    const item = this.item();
    if (item.subtitle) return item.subtitle;
    return this.transloco.translate(
      item.kind === 'nav' ? 'shell.cmdk.preview.nav_fallback' : 'shell.cmdk.preview.action_fallback',
      { name: item.title },
    );
  });

  protected readonly runLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate(
      this.item().kind === 'nav' ? 'shell.cmdk.preview.open' : 'shell.cmdk.preview.run',
    );
  });

  protected readonly triggerLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.preview.trigger_anywhere');
  });

  /** The shortcut with the ⌘ stripped — paper-kbd renders the real modifier. */
  protected readonly shortcutKeys = computed(() => {
    const raw = this.item().shortcut;
    return raw ? raw.replace('⌘', '') : null;
  });

  protected readonly usesModifier = computed(() => (this.item().shortcut ?? '').includes('⌘'));
}
